import restClient from "api/restClient";
import { convertTime } from "config/ts-utils";
import { useEffect, useRef, useState } from "react";

type RuntimeStats = {
    p75_seconds: number | null;
    sample_count: number;
    success_rate: number | null;
    trend_ratio: number | null;
    tier: number;
};

type Props = {
    executionId: string;
    startedAt: string;
    compact?: boolean;
};

export const RuntimeProgressBar = ({ executionId, startedAt, compact = false }: Props) => {
    const [stats, setStats] = useState<RuntimeStats | null>(null);
    const [elapsed, setElapsed] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        restClient
            .get(`/execution/${executionId}/stats`)
            .then((r) => setStats(r.data))
            .catch(() => {});
    }, [executionId]);

    useEffect(() => {
        if (!startedAt) return;
        const tick = () => setElapsed((Date.now() - Date.parse(startedAt)) / 1000);
        tick();
        intervalRef.current = setInterval(tick, 1000);
        return () => {
            if (intervalRef.current !== null) clearInterval(intervalRef.current);
        };
    }, [startedAt]);

    if (!stats?.p75_seconds) return null;

    const { p75_seconds, sample_count, success_rate, trend_ratio } = stats;
    const progress = (elapsed / p75_seconds) * 100;
    const isOverrun = progress > 100;
    const overrunSec = Math.max(0, elapsed - p75_seconds);
    const fillWidth = `${Math.min(progress, 100)}%`;
    const barColor = isOverrun ? "#e87040" : "#3bb6fd";

    if (compact) {
        return (
            <div style={{ width: "100%", marginTop: 3 }}>
                <div
                    style={{
                        position: "relative",
                        height: 3,
                        background: "#d0d0d0",
                        borderRadius: 2,
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            height: "100%",
                            width: fillWidth,
                            background: barColor,
                            transition: "width 1s linear",
                        }}
                    />
                </div>
                <div style={{ fontSize: "0.8em", opacity: 0.8, marginTop: 1 }}>
                    ~{convertTime(p75_seconds)} est.
                    {isOverrun && (
                        <span style={{ color: "#e87040" }}> (+{convertTime(overrunSec)})</span>
                    )}
                    {trend_ratio !== null && trend_ratio > 1.2 && (
                        <span style={{ color: "#e65100" }}> ▲</span>
                    )}
                    {trend_ratio !== null && trend_ratio < 0.8 && (
                        <span style={{ color: "#2e7d32" }}> ▼</span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: "100%", marginTop: 8 }}>
            <div
                style={{
                    position: "relative",
                    height: 6,
                    background: "#d0d0d0",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: fillWidth,
                        background: barColor,
                        transition: "width 1s linear",
                    }}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 4,
                    fontSize: "0.85em",
                }}
            >
                <span>
                    ~{convertTime(p75_seconds)} estimated
                    {isOverrun && (
                        <span style={{ color: "#e87040", marginLeft: 4 }}>
                            (+{convertTime(overrunSec)} over)
                        </span>
                    )}
                </span>
                <span style={{ opacity: 0.6 }}>{sample_count} past runs</span>
                {success_rate !== null && (
                    <span
                        style={{
                            color:
                                success_rate >= 0.9
                                    ? "#2e7d32"
                                    : success_rate >= 0.7
                                    ? "#e65100"
                                    : "#c62828",
                        }}
                    >
                        {Math.round(success_rate * 100)}% success
                    </span>
                )}
                {trend_ratio !== null && trend_ratio > 1.2 && (
                    <span style={{ color: "#e65100" }}>▲ slower recently</span>
                )}
                {trend_ratio !== null && trend_ratio < 0.8 && (
                    <span style={{ color: "#2e7d32" }}>▼ faster recently</span>
                )}
            </div>
        </div>
    );
};
